/**
 * @architecture-header 1
 * @layer module
 * @domain assistants
 * @responsibility List the teacher's AI assistants and hand the selected one to the editor.
 * @state-owner dataset store holds the personas; local state holds the unsaved new one
 * @allowed-imports React; app/providers/ActiveRoleProvider; app API mutations and queries; dataset and workspace stores; shared hooks; module model and components
 * @forbidden direct API client; student-only surfaces
 * @public-api settings dialog
 * @side-effects persona create, update, delete and default changes
 * @tests modules/assistants/model/assistant.test.ts and the workspace Playwright flows
 */

import { useEffect, useState } from 'react';
import {
  useActiveContextId,
  useActiveOrganizationId,
} from '../../../app/providers/ActiveRoleProvider';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import type { AssistantRecord } from '../../../shared/lib/model';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import {
  useCreateAssistant,
  useDeleteAssistant,
  useUpdateAssistant,
  useUpdateAssistantDefault,
} from '../api/assistants.mutations';
import { useAssistants } from '../api/assistants.queries';
import { assistantSummary, blankAssistant, defaultAssistant } from '../model/assistant';
import { AssistantEditor } from './AssistantEditor';

export const ASSISTANT_SETTINGS_FORM_ID = 'assistant-settings-form';

export interface AssistantSettingsActionState {
  readonly dirty: boolean;
  readonly saving: boolean;
}

const IDLE_ACTION_STATE: AssistantSettingsActionState = { dirty: false, saving: false };

export function AssistantSettingsPage({
  onActionStateChange,
  onSaved,
  savePlacement = 'inline',
}: {
  onActionStateChange?: (state: AssistantSettingsActionState) => void;
  onSaved?: () => void;
  savePlacement?: 'external' | 'inline';
} = {}) {
  const t = useTranslator();
  const activeContextId = useActiveContextId();
  const organizationId = useActiveOrganizationId();
  const assistantsQuery = useAssistants(activeContextId, organizationId, true);
  const assistants = useDatasetStore((state) => state.assistants);
  const projectedStatus = useDatasetStore((state) => state.assistantDirectoryStatus);
  const notify = useWorkspaceUiStore((state) => state.notify);
  /** A persona being created lives here until it validates; the store only holds usable ones. */
  const [pending, setPending] = useState<AssistantRecord | null>(null);
  const [selectedId, setSelectedId] = useState(() => defaultAssistant(assistants)?.id ?? '');
  const createAssistant = useCreateAssistant();
  const updateAssistant = useUpdateAssistant();
  const deleteAssistant = useDeleteAssistant();
  const updateAssistantDefault = useUpdateAssistantDefault();

  const editing = pending ?? assistants.find((item) => item.id === selectedId) ?? assistants[0];

  useEffect(() => {
    if (!editing) onActionStateChange?.(IDLE_ACTION_STATE);
  }, [editing, onActionStateChange]);

  if (assistantsQuery.isPending || projectedStatus === 'loading') {
    return <VispoLoading className="assistant-settings-state" label={t('正在載入 AI 助教')} />;
  }
  if (
    projectedStatus === 'denied' ||
    (assistantsQuery.isError && hasHttpStatus(assistantsQuery.error, 403))
  ) {
    return (
      <div className="assistant-settings-state" role="alert">
        <strong>{t('無法查看 AI 助教')}</strong>
        <span>{t('你沒有查看這個工作區 AI 助教的權限。')}</span>
      </div>
    );
  }
  if (projectedStatus === 'error' || assistantsQuery.isError) {
    return (
      <div className="assistant-settings-state" role="alert">
        <strong>{t('AI 助教清單暫時無法載入')}</strong>
        <button className="bt sm" type="button" onClick={() => void assistantsQuery.refetch()}>
          {t('重試')}
        </button>
      </div>
    );
  }

  const startNew = () => {
    const draft = blankAssistant(useDatasetStore.getState().allocateAssistantId());
    setPending(draft);
    setSelectedId(draft.id);
  };

  const save = async (assistant: AssistantRecord) => {
    const idempotencyKey = `agent-a-ast-f01-save-${assistant.id}-${assistant.revision}-${Date.now()}`;
    if (!organizationId) throw new Error('目前沒有可用的老師工作區');
    const previous = editing;
    const result = pending
      ? await createAssistant.mutateAsync({
          activeContextId,
          organizationId,
          assistant,
          idempotencyKey,
        })
      : await updateAssistant.mutateAsync({
          activeContextId,
          organizationId,
          assistant,
          idempotencyKey,
        });
    let committedResult = result;
    try {
      if (assistant.isDefault && !editing?.isDefault) {
        const defaultResult = await updateAssistantDefault.mutateAsync({
          activeContextId,
          organizationId,
          assistantId: result.id,
          idempotencyKey: `${idempotencyKey}-default`,
        });
        if (typeof defaultResult !== 'string') committedResult = defaultResult;
      }
    } catch (error) {
      if (pending && !result.isDefault) {
        await deleteAssistant.mutateAsync({
          activeContextId,
          organizationId,
          assistant: result,
          idempotencyKey: `${idempotencyKey}-compensate`,
        });
      } else if (!pending && previous) {
        await updateAssistant.mutateAsync({
          activeContextId,
          organizationId,
          assistant: { ...previous, revision: result.revision },
          idempotencyKey: `${idempotencyKey}-compensate`,
        });
      }
      throw error;
    }
    useDatasetStore.getState().saveAssistant(committedResult);
    setPending(null);
    setSelectedId(committedResult.id);
    notify(`${committedResult.name} ${t('已儲存')}`);
    onSaved?.();
  };

  const remove = async (assistant: AssistantRecord) => {
    if (pending?.id === assistant.id) {
      setPending(null);
      setSelectedId(defaultAssistant(assistants)?.id ?? '');
      return;
    }
    if (!organizationId) throw new Error('目前沒有可用的老師工作區');
    await deleteAssistant.mutateAsync({
      activeContextId,
      organizationId,
      assistant,
      idempotencyKey: `agent-a-ast-f01-delete-${assistant.id}-${assistant.revision}-${Date.now()}`,
    });
    useDatasetStore.getState().removeAssistant(assistant.id);
    setSelectedId(defaultAssistant(assistants)?.id ?? '');
    notify(`${assistant.name} ${t('已刪掉')}`);
  };

  const rows = pending ? [...assistants, pending] : assistants;

  return (
    <div className="asgrid">
      <aside className="aslist">
        <div className="asnew">
          <button className="bt sm pri" type="button" onClick={startNew}>
            {t('新增助教')}
          </button>
        </div>
        {rows.map((assistant) => (
          <button
            className={`asitem ${assistant.id === editing?.id ? 'on' : ''}`}
            key={assistant.id}
            type="button"
            onClick={() => {
              if (pending && pending.id !== assistant.id) setPending(null);
              setSelectedId(assistant.id);
            }}
          >
            <span className="asie" style={{ ['--accent' as string]: assistant.accent }}>
              {assistant.emoji}
            </span>
            <span className="asit">
              <b>
                {assistant.name || t('未命名助教')}
                {assistant.isDefault ? <em className="asbadge">{t('預設')}</em> : null}
              </b>
              <span>{assistant.name ? assistantSummary(assistant) : t('還沒設定完')}</span>
            </span>
          </button>
        ))}
        <p className="asnote">
          {t('派發活動時可以指定其中一位。沒有指定的活動一律由預設助教接手。')}
        </p>
      </aside>
      {editing ? (
        <AssistantEditor
          assistant={editing}
          formId={ASSISTANT_SETTINGS_FORM_ID}
          key={editing.id}
          onActionStateChange={onActionStateChange}
          onDelete={() => remove(editing)}
          onSave={save}
          savePlacement={savePlacement}
        />
      ) : null}
    </div>
  );
}
